// @flow

import * as React from 'react';
import {
    Pressable, StyleSheet, useWindowDimensions, View,
} from 'react-native';
import { useSelector } from 'react-redux';
import { defineMessages, FormattedMessage } from 'react-intl';
import debounce from 'lodash/debounce';
import { useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { APIContext, connectWS, getUser } from 'speed-js-react';
import { GRAY_COLOR, PRIMARY_COLOR } from 'coooltv-js-shared/src/styles/common';
import AbsoluteActivityIndicator from 'src/components/AbsoluteActivityIndicator';
import Title from 'src/components/Title';
import Profile from 'src/components/Profile';
import Device from 'src/components/Device';
import Text from 'src/components/CustomText/CustomText';
import {
    CREATE_DEVICE_SCREEN, CREATE_PROFILE_SCREEN, EDIT_DEVICE_SCREEN, EDIT_PROFILE_SCREEN, TRIAL_PERIOD_START,
} from 'src/navigation/constants';
import MainWrapper from 'src/components/MainWrapper';
import addProfile from 'src/Icons/Images/addProfile.svg';
import AddDeviceIcon from 'src/Icons/AddDeviceIcon';
import AccountSelector, { useLoadAccounts } from 'src/containers/AccountSelector';
import { navigateWithoutHistory } from 'src/navigation/Stack';
import Note from 'src/components/dialogs/Note';
import homeScreenStyles from 'src/styles/home';
import { BLUE_COLOR } from 'src/styles/common';

type Props = {
    ws: WebSocketClient,
}

const styles = StyleSheet.create({
    base_wrapper: {
        borderTopWidth: 1,
        borderRightStyle: 'solid',
        borderColor: PRIMARY_COLOR,
        flexWrap: 'wrap',
        marginTop: -2,
    },
    profile_wrapper: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
    },
    serviceWrapper: {
        paddingTop: 60,
    },
    pressableContent: {
        maxWidth: '100%',
    },
    activity_indicator_wrapper: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 20,
    },
    device_wrapper: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
    },
    active_text: {
        color: PRIMARY_COLOR,
        fontSize: 14,
    },
    stop_text: {
        color: BLUE_COLOR,
        fontSize: 14,
    },
    trialAccountContainer: {
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: 20,
    },
    trialTitle: {
        fontFamily: 'Open Sans',
        fontSize: 22,
        color: PRIMARY_COLOR,
        fontWeight: 300,
        paddingHorizontal: 150,
    },
    trialTitlePhone: {
        fontFamily: 'Open Sans',
        fontSize: 20,
        color: PRIMARY_COLOR,
        fontWeight: 300,
        paddingHorizontal: 20,
    },
    trialText: {
        fontFamily: 'Open Sans',
        fontSize: 22,
        color: GRAY_COLOR,
        fontWeight: 300,
        marginTop: 15,
        paddingHorizontal: 150,
    },
    trialTextPhone: {
        fontFamily: 'Open Sans',
        fontSize: 20,
        color: GRAY_COLOR,
        fontWeight: 300,
        marginTop: 15,
        paddingHorizontal: 20,
    },
});

const messages = defineMessages({
    trialAccountMessage: {
        id: 'trial-account-message',
        defaultMessage: 'You are testing coool.tv for free for the next {hours} hours. Activation of'
            + 'a regular service can be done through My Cooolbox.',
    },
});

const WS_GROUP = 'create.devices.device_session';

const Home = (props: Props): React.Node => {
    const { ws } = props;
    const { apiClient } = React.useContext(APIContext);
    const { width } = useWindowDimensions();
    const [profilesResult, setProfiles] = React.useState(null);
    const [devicesResult, setDevices] = React.useState(null);
    const [accountsLoaded, allAccounts, currentAccount, setCurrentAccount] = useLoadAccounts();
    const user = useSelector(getUser);

    const isFocused = useIsFocused();
    const navigation = useNavigation();
    const route = useRoute();

    const currentAccountUuid = currentAccount?.uuid;

    React.useEffect(() => {
        document.getElementById('page-description').setAttribute('content', `
        Бързо, лесно и удобно създаване и управление на потребителски профили, добавяне на устройства, 
        подредба на канали по предпочитание.
        `);
    }, []);

    React.useEffect(() => {
        const prevUrl = sessionStorage.getItem('prev_url');
        if (prevUrl) {
            sessionStorage.removeItem('prev_url');
            try {
                navigateWithoutHistory(navigation, prevUrl);
                // eslint-disable-next-line no-empty
            } catch (Error) {}
        }
    }, [navigation]);

    React.useEffect(() => {
        if (accountsLoaded && !allAccounts.length) {
            navigateWithoutHistory(navigation, TRIAL_PERIOD_START);
        }
    }, [accountsLoaded, allAccounts, navigation]);

    const handleAccountSelect = React.useCallback((account) => {
        setCurrentAccount(account);
    }, [setCurrentAccount]);

    const getDevices = React.useCallback(() => {
        if (currentAccountUuid) {
            apiClient.resources.devices.list({ account_id: currentAccountUuid })
                .then((res) => {
                    setDevices(res.results);
                });
        }
    }, [apiClient.resources.devices, currentAccountUuid]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleWS = React.useCallback(debounce((event) => {
        if (event.event_id === currentAccountUuid) {
            getDevices();
        }
    }, 300), [getDevices, currentAccountUuid]);

    React.useEffect(() => {
        if (currentAccountUuid) {
            ws.subscribe({
                [WS_GROUP]: handleWS,
            });
            return () => {
                ws.unSubscribe([WS_GROUP]);
            };
        }
        return () => {};
    }, [ws, handleWS, currentAccountUuid]);

    React.useEffect(() => {
        if (isFocused && user && currentAccount) {
            apiClient.resources.accountProfiles.list(currentAccount.uuid)
                .then((res) => {
                    setProfiles(res.results);
                });
            getDevices();
        }
    }, [apiClient.resources.accountProfiles, getDevices, isFocused, user, currentAccount]);

    const getTrialExpirationHours = React.useCallback((account) => {
        const dateNow = new Date();
        const accountExpirationDate = new Date(account.trial_expiration_date);
        return Math.ceil(Math.abs(dateNow - accountExpirationDate) / 36e5);
    }, []);

    if (!user || !accountsLoaded) {
        return <AbsoluteActivityIndicator />;
    }

    return (
        <View>
            <MainWrapper>
                {((route.params?.profileUpdated && route.params?.profileUpdated !== 'false')
                    || (route.params?.deviceUpdated && route.params?.deviceUpdated !== 'false')) && (
                    <Note
                        text={route.params?.profileUpdated
                            ? (
                                <FormattedMessage
                                    id="profile-change-message"
                                    defaultMessage={'Your profile has been updated successfully!'
                                    + 'To see the changes in the coool.tv app please reload the profile'}
                                />
                            ) : (
                                <FormattedMessage
                                    id="device-change-message"
                                    defaultMessage="Your device has been updated successfully!"
                                />
                            )}
                        onClose={() => (route.params?.profileUpdated
                            ? navigation.setParams({ profileUpdated: false })
                            : navigation.setParams({ deviceUpdated: false }))}
                    />
                )}
                {currentAccount?.is_trial && (
                    <View style={styles.trialAccountContainer}>
                        <Text style={width > 600 ? styles.trialTitle : styles.trialTitlePhone}>
                            <FormattedMessage
                                id="activated-trial-account"
                                defaultMessage="Activated Trial Period"
                            />
                        </Text>
                        <Text style={width > 600 ? styles.trialText : styles.trialTextPhone}>
                            <FormattedMessage
                                {...messages.trialAccountMessage}
                                values={{ hours: getTrialExpirationHours(currentAccount) }}
                            />
                        </Text>
                    </View>
                )}
                <Title
                    text={(
                        <FormattedMessage
                            id="service"
                            defaultMessage="Service"
                        />
                    )}
                />
                <View style={[styles.base_wrapper, styles.serviceWrapper]}>
                    <AccountSelector
                        allAccounts={allAccounts}
                        currentAccount={currentAccount}
                        onAccountSelect={handleAccountSelect}
                    />
                </View>

                <Title text={(
                    <FormattedMessage
                        id="profiles-and-parent-control"
                        defaultMessage="Profiles and Parent Control"
                    />
                )}
                />
                <View style={[styles.base_wrapper, styles.profile_wrapper]}>
                    {profilesResult ? profilesResult.map((el) => (
                        <Pressable
                            disabled={el.is_default}
                            onPress={() => navigateWithoutHistory(
                                navigation, EDIT_PROFILE_SCREEN, { uuid: el.uuid },
                            )}
                            key={el.uuid}
                            style={styles.pressableContent}
                        >
                            <Profile name={el.name} imageUrl={el.white_image_url} />
                        </Pressable>
                    )) : null }
                    <Pressable onPress={() => navigateWithoutHistory(navigation, CREATE_PROFILE_SCREEN)}>
                        <Profile imageUrl={addProfile} name={<FormattedMessage id="add" defaultMessage="Add" />} />
                    </Pressable>
                </View>
                <Title text={
                    (
                        <FormattedMessage
                            id="devices"
                            defaultMessage="Devices"
                        />
                    )
                }
                />
                <View style={[styles.base_wrapper, styles.profile_wrapper]}>
                    {devicesResult ? devicesResult.map((el) => (
                        <View style={styles.device_wrapper} key={el.uuid}>
                            <Pressable
                                onPress={() => navigateWithoutHistory(
                                    navigation, EDIT_DEVICE_SCREEN, { uuid: el.uuid },
                                )}
                            >
                                <Device
                                    name={el.name}
                                />
                            </Pressable>
                            {el.active_session && (
                                <View style={styles.activity_indicator_wrapper}>
                                    <Text style={styles.active_text}>
                                        <FormattedMessage id="active-session" defaultMessage="Active" />
                                    </Text>
                                    <Pressable
                                        key={el.uuid}
                                        onPress={() => apiClient.resources.deviceSession.end(el.active_session).then(
                                            () => getDevices(),
                                        )}
                                    >
                                        <Text style={styles.stop_text}>
                                            <FormattedMessage id="stop-session" defaultMessage="Stop" />
                                        </Text>
                                    </Pressable>
                                </View>
                            )}
                        </View>
                    )) : null}
                    <Pressable onPress={() => navigateWithoutHistory(navigation, CREATE_DEVICE_SCREEN)}>
                        <View style={homeScreenStyles.image_container}>
                            <View style={homeScreenStyles.image_wrapper}>
                                <AddDeviceIcon />
                            </View>
                            <Text
                                style={homeScreenStyles.device_text}
                            >
                                <FormattedMessage id="add" defaultMessage="Add" />
                            </Text>
                        </View>
                    </Pressable>
                </View>
            </MainWrapper>
        </View>
    );
};

export default connectWS(Home);
